import { request } from '@umijs/max';

export async function fetchUsers(): Promise<API.AdminUser[]> {
  const res = await request<API.UserListResponse>('/api/users');
  return res.users;
}

export async function fetchDepartments(): Promise<string[]> {
  const res = await request<API.DepartmentsResponse>('/api/users/departments');
  return res.departments;
}

export async function createUser(
  payload: API.UserCreatePayload,
): Promise<API.UserCreateResponse> {
  return request<API.UserCreateResponse>('/api/users', {
    method: 'POST',
    data: payload,
  });
}

export async function updateUser(
  id: string,
  payload: API.UserUpdatePayload,
): Promise<API.AdminUser> {
  return request<API.AdminUser>(`/api/users/${id}`, {
    method: 'PUT',
    data: payload,
  });
}

export async function deleteUser(id: string): Promise<void> {
  await request(`/api/users/${id}`, { method: 'DELETE' });
}

export async function toggleUserStatus(id: string): Promise<API.AdminUser> {
  return request<API.AdminUser>(`/api/users/${id}/toggle-status`, {
    method: 'PATCH',
  });
}

export async function fetchUserPermissions(id: string): Promise<API.UserPermissions> {
  const [branchesRes, reports, modules] = await Promise.all([
    request<API.BranchPermissionsResponse>(`/api/users/${id}/branches`),
    request<API.StringListResponse>(`/api/users/${id}/reports`),
    request<API.StringListResponse>(`/api/users/${id}/modules`),
  ]);
  const branchItems = branchesRes.branches ?? [];
  return {
    branches: branchItems.map((b) => b.branch_code),
    branch_sources: Object.fromEntries(branchItems.map((b) => [b.branch_code, b.source])),
    reports: reports.reports ?? [],
    module_ids: modules.modules ?? [],
  };
}

export async function syncUsersFromArms(): Promise<API.UserSyncEnqueueResponse> {
  return request<API.UserSyncEnqueueResponse>('/api/users/sync-from-arms', {
    method: 'POST',
  });
}

export async function fetchUserSyncStatus(runId: string): Promise<API.UserSyncStatusResponse> {
  return request<API.UserSyncStatusResponse>(`/api/users/sync-from-arms/${runId}`);
}

export async function fetchUserSyncHistory(
  limit = 20,
): Promise<API.UserSyncHistoryResponse> {
  return request<API.UserSyncHistoryResponse>('/api/users/sync-from-arms/history', {
    params: { limit },
  });
}

export async function unlinkUserFromArms(id: string): Promise<API.AdminUser> {
  return request<API.AdminUser>(`/api/users/${id}/unlink-arms`, {
    method: 'POST',
  });
}

export async function saveUserPermissions(
  id: string,
  payload: API.UserPermissions,
): Promise<void> {
  await request(`/api/users/${id}/permissions`, {
    method: 'PUT',
    data: payload,
  });
}

export async function bulkSavePermissions(
  payload: API.BulkPermissionsPayload,
): Promise<API.BulkPermissionsResponse> {
  return request<API.BulkPermissionsResponse>('/api/users/bulk/permissions', {
    method: 'POST',
    data: payload,
  });
}

export async function triggerPasswordReset(
  id: string,
  payload?: API.TriggerPasswordResetPayload,
): Promise<API.MessageResponse> {
  return request<API.MessageResponse>(`/api/users/${id}/trigger-password-reset`, {
    method: 'POST',
    data: payload ?? {},
  });
}

export async function bulkTriggerPasswordReset(
  userIds: string[],
): Promise<API.BulkPasswordResetResponse> {
  return request<API.BulkPasswordResetResponse>('/api/users/bulk-trigger-password-reset', {
    method: 'POST',
    data: { user_ids: userIds },
  });
}

export async function fetchAvailableReports(): Promise<API.ReportDefinition[]> {
  const res = await request<API.ReportsAvailableResponse>('/api/reports/available');
  return res.reports;
}

export async function fetchAvailableModules(): Promise<API.ModuleDefinition[]> {
  const res = await request<API.ModulesAvailableResponse>('/api/modules/available');
  return res.modules;
}

export async function fetchMyReports(): Promise<string[]> {
  const res = await request<API.StringListResponse>('/api/users/me/reports');
  return res.reports ?? [];
}

export async function fetchMyModules(): Promise<string[]> {
  const res = await request<API.StringListResponse>('/api/users/me/modules');
  return res.modules ?? [];
}

export async function validateImportFile(file: File): Promise<API.ExcelValidateResponse> {
  const form = new FormData();
  form.append('file', file);
  return request<API.ExcelValidateResponse>('/api/users/validate-excel', {
    method: 'POST',
    data: form,
  });
}

export async function importUsers(
  file: File,
  fieldMapping: Record<string, string>,
): Promise<API.UserImportResponse> {
  const form = new FormData();
  form.append('file', file);
  form.append('field_mapping', JSON.stringify(fieldMapping));
  return request<API.UserImportResponse>('/api/users/import', {
    method: 'POST',
    data: form,
  });
}

export function importTemplateUrl(): string {
  return '/api/users/import-template';
}

export function exportUsersUrl(): string {
  return '/api/users/export';
}
